import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'Monolith-cart';

function loadCart() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

export function getCartLineId(productId, size) {
  return size ? `${productId}::${size}` : productId;
}

function matchesCartLine(item, cartLineId) {
  return getCartLineId(item.id, item.size) === cartLineId;
}

function resolveMaxQuantity(item, fallbackStock) {
  const stock = Number(
    item?.availableStock != null ? item.availableStock : fallbackStock
  );
  if (!Number.isFinite(stock) || stock < 0) return null;
  return stock;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    const qty = Math.max(1, Number(quantity) || 1);
    const cartLineId = getCartLineId(product.id, product.size);
    const maxQuantity = resolveMaxQuantity(product);

    setCartItems(current => {
      const existing = current.find(item => matchesCartLine(item, cartLineId));
      const currentQty = existing ? Number(existing.quantity) || 0 : 0;
      const nextQty =
        maxQuantity == null
          ? currentQty + qty
          : Math.min(currentQty + qty, maxQuantity);

      if (nextQty < 1) {
        return current;
      }

      if (existing) {
        if (nextQty === currentQty) return current;
        return current.map(item =>
          matchesCartLine(item, cartLineId)
            ? {
                ...item,
                quantity: nextQty,
                availableStock:
                  product.availableStock != null
                    ? product.availableStock
                    : item.availableStock,
              }
            : item
        );
      }

      return [
        ...current,
        {
          ...product,
          cartLineId,
          quantity: maxQuantity == null ? qty : Math.min(qty, maxQuantity),
        },
      ];
    });
  };

  const removeFromCart = cartLineId => {
    setCartItems(current => current.filter(item => !matchesCartLine(item, cartLineId)));
  };

  const updateQuantity = (cartLineId, quantity) => {
    if (quantity < 1) {
      removeFromCart(cartLineId);
      return;
    }

    setCartItems(current =>
      current.map(item => {
        if (!matchesCartLine(item, cartLineId)) return item;
        const maxQuantity = resolveMaxQuantity(item);
        const nextQty =
          maxQuantity == null ? quantity : Math.min(quantity, maxQuantity);
        return { ...item, quantity: nextQty };
      })
    );
  };

  const clearCart = () => setCartItems([]);

  const totalCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const totalPrice = useMemo(
    () => cartItems.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalCount,
    totalPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}
